import BlueprintUtils from '../blueprintUtils.js'
import RenderingMath from '../compilers/RenderingMath'
import RichPlaceholderCompiler from '../compilers/RichPlaceholderCompiler'
import { DEFAULT_BACKGROUND, WIDGET_PREVIEW_SIZE } from '../constants'
import DomainService from 'domain-service'
import AssetPathService from 'asset-path-service'

const DEFAULT_OBJECT_FIT = 'contain'
const DEFAULT_OBJECT_POSITION = 'center center'

function computeBorderlessRegion(widget) {
  const widgetLayout = widget.layout
  const widgetBaseStyle = widget.baseStyle
  const borderStyle = widgetBaseStyle['border-style']
  const borderWidth =
    borderStyle && borderStyle !== 'none' && typeof widgetBaseStyle['border-width'] === 'number'
      ? widgetBaseStyle['border-width']
      : 0
  const doubleBorderWidth = 2 * borderWidth

  if (doubleBorderWidth >= Math.min(widgetLayout.width, widgetLayout.height)) {
    // return false if specified border-width is not going to leave any content space...
    return false
  }

  return {
    width: widgetLayout.width - doubleBorderWidth,
    height: widgetLayout.height - doubleBorderWidth,
    x: widgetLayout.x + borderWidth,
    y: widgetLayout.y + borderWidth,
  }
}

function computeAlignments(widget) {
  const widgetBaseStyle = widget.baseStyle

  //FIXME sanitize null/undefined values should be done in earlier stage of processing
  const objectPosition = widgetBaseStyle['object-position'] || DEFAULT_OBJECT_POSITION
  let objectPositionTokens = objectPosition.split(' ')
  if (objectPositionTokens.length !== 2) {
    objectPositionTokens = DEFAULT_OBJECT_POSITION.split(' ')
  }
  const valign = objectPositionTokens[0]
  const halign = objectPositionTokens[1]

  return {
    valign,
    halign,
  }
}

function computeClippedRegion(widget, clipBox, skipClip) {
  if (skipClip || !clipBox || !clipBox.width || !clipBox.height) {
    return false
  }

  const borderlessLayout = computeBorderlessRegion(widget)

  // disable clipping when borders eat away all available content
  if (!borderlessLayout) {
    return false
  }

  // false means nothing would be clipped
  let result = false

  // Original abstraction by Hubert - moved here as only this file uses it
  // Helper to determine amount of overlap between two different ranges
  // Used only to determine if image is clipped by boundaries of template
  // Returns false if there is no overlap
  // FIXME - This function can return either a bool or a obj - not type safe
  function _getOverlappedInterval(interval1, interval2) {
    if (RenderingMath.isOverlapped(interval1, interval2)) {
      return {
        left: Math.max(interval1.left, interval2.left),
        right: Math.min(interval1.right, interval2.right),
      }
    } else {
      return false
    }
  }
  const xOverlap = _getOverlappedInterval(
    { left: borderlessLayout.x, right: borderlessLayout.x + borderlessLayout.width },
    { left: 0, right: clipBox.width }
  )
  const yOverlap = _getOverlappedInterval(
    { left: borderlessLayout.y, right: borderlessLayout.y + borderlessLayout.height },
    { left: 0, right: clipBox.height }
  )

  if (xOverlap && yOverlap) {
    result = {
      // region dimension and position are used to compute the css styles on html export
      width: xOverlap.right - xOverlap.left,
      height: yOverlap.right - yOverlap.left,
      left: xOverlap.left,
      top: yOverlap.left,

      // interval objects are used to compute parameters for image-resize-api
      xOverlap,
      yOverlap,
    }
  }

  return result
}

function computeImageResizeParameters(widget, clippedRegion, skipClip) {
  const widgetLayout = widget.layout
  const borderlessLayout = computeBorderlessRegion(widget)

  let width, height
  let cropAreaX1, cropAreaX2, cropAreaY1, cropAreaY2
  if (!skipClip && borderlessLayout && clippedRegion) {
    width = borderlessLayout.width
    height = borderlessLayout.height
    cropAreaX1 = clippedRegion.xOverlap.left - borderlessLayout.x
    cropAreaX2 = clippedRegion.xOverlap.right - borderlessLayout.x
    cropAreaY1 = clippedRegion.yOverlap.left - borderlessLayout.y
    cropAreaY2 = clippedRegion.yOverlap.right - borderlessLayout.y
  } else {
    width = widgetLayout.width
    height = widgetLayout.height
    cropAreaX1 = 0
    cropAreaX2 = widgetLayout.width
    cropAreaY1 = 0
    cropAreaY2 = widgetLayout.height
  }

  const adjustedLayout = {
    width: Math.max(1, width),
    height: Math.max(1, height),
    cropAreaX1,
    cropAreaX2,
    cropAreaY1,
    cropAreaY2,
  }

  // Ensure all values are integer
  for (let [key, value] of Object.entries(adjustedLayout)) {
    adjustedLayout[key] = Math.round(value)
  }

  return Object.assign({}, adjustedLayout, computeAlignments(widget))
}

function parseElementStyle(props) {
  const { widget, isHyperlinkEnabled, zModifier, isRenderingInsideLayerTool, isRenderingInsideAceEditor } = props
  let widgetLayout = widget.layout || {}
  let widgetBaseStyle = widget.baseStyle ? JSON.parse(JSON.stringify(widget.baseStyle)) : {}
  let animationTypeMap = {}
  // In preparation for multiple animations, animation fill mode is converted to 'forwards' instead of 'both'.
  // This is because the 'both' fill mode does not work with multiple animations. This then requires us to set
  // the position of the actual widget to the starting point of the animation otherwise the widget will become visible
  // in the end position of the animation and not the starting position.
  if (widget.animations?.length && !isRenderingInsideAceEditor) {
    widget.animations.forEach(a => {
      if (
        (a.type !== 'transition' && animationTypeMap[a.type]) ||
        (a.type === 'transition' && animationTypeMap[a.targetStyle.name])
      ) {
        return
      }

      switch (true) {
        case a.type === 'move' || a.type === 'resize':
          animationTypeMap['move'] = true
          animationTypeMap['resize'] = true
          widgetLayout = {
            ...widgetLayout,
            x: a.animationStates[0].layout.x,
            y: a.animationStates[0].layout.y,
            width: a.animationStates[0].layout.width,
            height: a.animationStates[0].layout.height,
          }
          break
        case a.type === 'transition':
          animationTypeMap[a.targetStyle.name] = true
          widgetBaseStyle[a.targetStyle.name] = a.animationStates[0].transition.value
          break
        case a.type === 'rotation':
          animationTypeMap[a.type] = true
          widgetLayout = {
            ...widgetLayout,
            zRotation: a.animationStates[0].rotation.value,
          }
          widgetBaseStyle.horizontal = a.animationStates[0].horizontal.value
          widgetBaseStyle.vertical = a.animationStates[0].vertical.value
          break
      }
    })
  }

  // if inside ace editor, set to current base style, not starting point of animation
  if (isRenderingInsideAceEditor) {
    widgetBaseStyle = widget.baseStyle
  }
  const scale =
    isRenderingInsideLayerTool && WIDGET_PREVIEW_SIZE / Math.max(widgetLayout.width, widgetLayout.height) < 1
      ? `scale(${WIDGET_PREVIEW_SIZE / Math.max(widgetLayout.width, widgetLayout.height)})`
      : ''
  const hasRotationAnimation = widget.animations?.some(a => a.type === 'rotation')
  // only scale is applied when displayed in layer tool
  let transformProperty
  if (isRenderingInsideLayerTool) {
    transformProperty = `${scale}`
  } else if (hasRotationAnimation) {
    transformProperty = `rotate(${widgetLayout.zRotation || 0}deg) rotateY(${widgetBaseStyle.horizontal ||
      0}deg) rotateX(${widgetBaseStyle.vertical || 0}deg)`
  } else {
    transformProperty = `rotate(${widgetLayout.zRotation || 0}deg)`
  }

  const st = {
    position: 'absolute',
    overflow: 'hidden',

    // Allow link-less widgets to be non-interactive, so that user could
    // interact with underlying links
    'pointer-events': isHyperlinkEnabled && !widget.href && 'none',

    'box-sizing': 'border-box',
    width: `${widgetLayout.width}px`,
    height: `${widgetLayout.height}px`,
    top: isRenderingInsideLayerTool ? '' : `${widgetLayout.y}px`,
    left: isRenderingInsideLayerTool ? '' : `${widgetLayout.x}px`,

    'z-index': widgetLayout.z + zModifier,

    background: widgetBaseStyle.background || DEFAULT_BACKGROUND,
    // 0 values do not get rendered so we use percent for 0 values
    opacity: widgetBaseStyle.opacity === 0 ? '0%' : widgetBaseStyle.opacity || 1,
    // hide widget if visibility off and widget is inside template (does not hide widget from actual ad)
    // strict false check is necessary as isLayerVisible undefined should default to true (DO NOT CHANGE to falsy !widget.isLayerVisible check)
    // widget visibility by default should be true
    display: widget.isLayerVisible === false && isRenderingInsideAceEditor && !isRenderingInsideLayerTool ? 'none' : '',

    'border-width': `${widgetBaseStyle['border-width'] || 0}px`,
    'border-radius': `${widgetBaseStyle['border-radius'] * 100 || 0}px`,
    'border-style': widgetBaseStyle['border-style'],
    'border-color': widgetBaseStyle['border-color'],

    transform: transformProperty,
  }

  return st
}

function parseClippedRegionStyle(widget, clippedRegion) {
  // TODO: current resizer with rotation emits weird behaviour.
  // Until issue is resolved, we ignore clipping on rotated images and gifs
  const ignoreClip =
    widget.layout.zRotation ||
    (typeof widget.value.content === 'string' && widget.value.content.endsWith('.gif')) ||
    (typeof widget.value.content === 'object' && widget.value.content.defaultValue.endsWith('.gif'))

  const borderlessLayout = computeBorderlessRegion(widget)

  return {
    position: `${ignoreClip ? '' : 'absolute'}`,
    width: `${ignoreClip ? '100%' : clippedRegion.width + 'px'}`,
    height: `${ignoreClip ? '100%' : clippedRegion.height + 'px'}`,
    left: `${ignoreClip ? '' : clippedRegion.left - borderlessLayout.x + 'px'}`,
    top: `${ignoreClip ? '' : clippedRegion.top - borderlessLayout.y + 'px'}`,
  }
}

function parseImageStyle(widget) {
  const widgetBaseStyle = widget.baseStyle || {}
  const objectPosition = widgetBaseStyle['object-position'] || DEFAULT_OBJECT_POSITION
  let objectPositionTokens = objectPosition.split(' ')
  // object-position should have 2 tokens, if there are more or less tokens, use default object-position
  if (objectPositionTokens.length !== 2) {
    objectPositionTokens = DEFAULT_OBJECT_POSITION.split(' ')
  }
  let halign = objectPositionTokens[0]
  let valign = objectPositionTokens[1]
  const hasRotationAnimation = widget.animations?.some(a => a.type === 'rotation')

  // reverse alignments if image is flipped
  if (widgetBaseStyle.horizontal) {
    switch (halign) {
      case 'left':
        halign = 'right'
        break
      case 'right':
        halign = 'left'
        break
      default:
        halign = 'center'
        break
    }
  }

  if (widgetBaseStyle.vertical) {
    switch (valign) {
      case 'top':
        valign = 'bottom'
        break
      case 'bottom':
        valign = 'top'
        break
      default:
        valign = 'center'
        break
    }
  }
  return {
    // Setting the dimension to 100% is required for sizing image object
    width: '100%',
    height: '100%',

    // default to "contain", which would resize the image to "fit" within the given space
    // while maintaining the aspect ratio. This __might__ lead to gaps
    'object-fit': widgetBaseStyle['object-fit'] || DEFAULT_OBJECT_FIT,
    'object-position': `${halign} ${valign}`,
    transform: hasRotationAnimation ? '' : `rotateY(${widgetBaseStyle.horizontal || 0}deg) rotateX(${widgetBaseStyle.vertical || 0}deg)`,
  }
}

function withClipped(sourceElement, $h, clippedRegion) {
  if (clippedRegion) {
    // Wrap the source element with two more layers

    // Limit the image dimension and define the relative position of clipped image
    const clippedRegionElement = $h(
      'div',
      {
        style: parseClippedRegionStyle(this.widget, clippedRegion),
        attrs: {
          'data-adacadotransitiontype': 'transition',
          'data-adacadopropname': this.widget.value.content.name
            ? `${this.widget.value.content.name}${this.widget.id}`
            : false,
        },
      },
      [sourceElement]
    )

    // Ensure the image is absolutely-position in respect to the main container
    const clippedRegionParentElement = $h(
      'div',
      {
        style: {
          position: 'relative',
          width: '100%',
          height: '100%',
        },
      },
      [clippedRegionElement]
    )
    return clippedRegionParentElement
  } else {
    return sourceElement
  }
}

function withDivContainer(sourceElement, $h) {
  //context was bound to this
  const props = this.props
  //account for any native events the template editor is trying to apply (ie mouseover, contextmenu)
  const nativeOn = (this.data && this.data.nativeOn) || {}

  // inventoryIndex from prop contains correct inventory index information
  // apply to widget.rule before compiling
  let widgetRule = this.props.widget.rule
  if (widgetRule) {
    widgetRule.inventoryIndex = this.props.inventoryIndex
  }
  return $h(
    'div',
    {
      on: {
        ...nativeOn,
      },
      style: parseElementStyle(props),
      RHStyles: widgetRule ? escape(this.props.contentCompiler.compile(widgetRule)) : '',
      attrs: {
        'widget-id': `widget${props.widget.id}`,
        class: props.animationClass,
      },
    },
    [sourceElement]
  )
}

function escape(rawString) {
  return rawString.replace(/[<>]/g, substr => {
    return (
      {
        '<': '&lt;',
        '>': '&gt;',
      }[substr] || substr
    )
  })
}

function escapeLinkAttribute(rawString) {
  return rawString.replace(/[<>]/g, substr => {
    return (
      {
        '<': '&lt;',
        '>': '&gt;',
      }[substr] || substr
    )
  })
}

function withAnchorContainer(sourceElement, $h) {
  // Wrap widget in an anchor tag if there is a value for href property
  if (this.isHyperlinkEnabled && this.widget.href) {
    // convert older widget hrefs into RP hrefs
    if (this.widget.meta?.widgetVersion === 1 && typeof this.widget.href === 'string') {
      // set href defaultValue to null if empty string
      this.widget.href = BlueprintUtils.create.richPlaceholderHref(this.widget.href ? this.widget.href : null)
    }
    // inventoryIndex from prop contains correct inventory index information
    // apply to widget.href before compiling html
    let widgetHref = JSON.parse(JSON.stringify(this.widget.href))
    widgetHref.inventoryIndex = this.inventoryIndex
    return $h(
      'a',
      {
        attrs: {
          // href attributes of widget are completely controlled by the end-user,
          // so it is important to sanitize the inputs in all cases.
          // FIXME: we are performing bare minimum checks that replaces `<` and `>`
          // Due to lack of support from vue(see  https://github.com/vuejs/vue/issues/8657),
          // we would have to incorporate other libraries for a complete sanitization logic
          href: escapeLinkAttribute(this.contentCompiler.compile(widgetHref, { isURL: true })),
          target: '_blank',
        },
      },
      [sourceElement]
    )
  } else {
    // when isHyperlinkEnabled is true AND widget.href is not defined,
    // pointer-events attribute will be set to "none" explicitly at method "parseElementStyle(...)"
    //
    // this allows the widget to inherit link behavior from the background
    return sourceElement
  }
}

export default {
  functional: true,
  props: {
    widget: {
      type: Object,
      required: true,
    },
    animationClass: {
      type: String,
      default: '',
    },
    templateId: Number,
    isRenderingInsideAceEditor: {
      type: Boolean,
      default: false,
    },
    isRenderingInsideLayerTool: {
      type: Boolean,
      default: false,
    },
    isHyperlinkEnabled: Boolean,
    clipBox: {
      type: Object,
      default() {
        return {}
      },
    },
    // CanvasTranspiler will pass this a prop where the RichPlaceholderCompiler's mode is set to 'placeholder'
    // which generates richplaceholders in the output html as opposed to just the default value (for viewing in Ace Editor)
    contentCompiler: {
      type: Object,
      default() {
        return new RichPlaceholderCompiler()
      },
    },
    zModifier: {
      type: Number,
      default: 0,
    },
    inventoryIndex: {
      type: Number,
      default: 0,
    },
    themeStaticData: {
      type: Array,
      default() {
        return []
      },
    },
    themeId: {
      type: [String, Number],
      default: undefined,
    },
  },
  render($h, context) {
    const {
      widget: w,
      templateId,
      isRenderingInsideAceEditor,
      clipBox,
      contentCompiler,
      inventoryIndex,
      themeStaticData = [],
      themeId,
    } = context.props
    // convert company logo or cta image button widgets which have value.content as just the image link back into RP object
    if (BlueprintUtils.lookup.isNotDataImageRPButValidForAllEdit(w) && !BlueprintUtils.lookup.isValidForAllEdit(w)) {
      BlueprintUtils.set.logoOrCTAImageWidgetRPContent(w)
    }

    const widget = JSON.parse(JSON.stringify(w))
    const widgetStaticData = themeStaticData.find(d => d.type.includes('image') && d.name == widget?.value.content.name)
    // do not clip image if widget has any animation that is not a transition
    const skipClip = !!widget?.animations?.find(a => a.type !== 'transition')
    const clippedRegion = computeClippedRegion(widget, clipBox, skipClip)

    // Inject inventoryIndex value for rich placeholder compiler
    // Remember, widget.value.content is sometimes and OBJECT, sometimes a STRING - FIXME
    if (widget.value && typeof widget.value.content == 'object') {
      widget.value.content.defaultValue = widgetStaticData ? widgetStaticData.value : widget.value.content.defaultValue
      widget.value.content.inventoryIndex = inventoryIndex
    }
    if (widget.href && typeof widget.href == 'object') {
      widget.href.inventoryIndex = inventoryIndex
    }

    let src = DomainService.decorateWithContentServerDomain`${contentCompiler.compileImageSource(
      widget.value.content,
      computeImageResizeParameters(widget, clippedRegion, skipClip),
      widget
    )}`
    // Only apply assetPath here if we're rendering to Ace-Editor, otherwise leave abstractPath in output html
    if (widgetStaticData) {
      src = src.split('/abstractThemePath').join(AssetPathService.getThemeAssetPath(themeId))
    } else if (isRenderingInsideAceEditor) {
      src = src
        .split('/abstractTemplatePath')
        .join(AssetPathService.getTemplateAssetPath(templateId))
      src = src
        .split('/abstractQuickStartTemplatePath')
        .join(AssetPathService.getQuickStartTemplateAssetPath(templateId))
    }
    const imageElement = $h('img', {
      attrs: {
        src,
        draggable: false,
      },
      style: parseImageStyle(widget),
    })

    // decorate with clip-container, then div, and then finally anchor
    return withAnchorContainer.call(
      context.props,
      withDivContainer.call(context, withClipped.call(context.props, imageElement, $h, clippedRegion), $h),
      $h
    )
  },
}
